import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { setAdjustedSummary } from '../../../store/slice/renewalHubSlice';
import { PRODUCT_CODE } from '../../../constants/renewalHub';
import {
  FREQUENCY_CODE_ANNUALLY,
  FREQUENCY_CODE_MONTHLY,
  FREQUENCY_CODE_FORTNIGHTLY
} from '../../../constants/constants';
import { decodeHtmlEntities } from '../../../utils/placeholderUtils';
import { getGtmService } from '../../../services/gtmService/GtmService';

// Fields that legitimately reach handleChange. Anything unlisted is skipped rather than
// mislabelled - a missing event is a visible gap, a mislabelled one is silent corruption.
const TRACKED_CHANGE_FIELD_NAMES = {
  RoutineCare: 'routine_care',
  BoosterCare: 'booster_care',
  BenefitPercentage: 'benefit_percentage',
  Excess: 'excess'
};

// Single fallback when a help control carries no item id; per-control wording would split one
// topic across several field_name values.
const HELP_TOPIC_UNKNOWN = 'unknown_help_topic';

// Both help controls inherit the same CMS item's data-item-id through the DOM, so closest() gives
// one stable key per topic whichever control the customer used.
const resolveHelpTopicId = (element) =>
  element?.closest('[data-item-id]')?.getAttribute('data-item-id') || HELP_TOPIC_UNKNOWN;

export const useRenewalAdjustCover = (renewalSummaryData, renewalQuoteData) => {
  const dispatch = useDispatch();

  // The tooltip effects below register their listeners once, so they would not see later
  // renewalSummaryData. A ref avoids widening their deps and rebinding document listeners.
  const renewalSummaryDataRef = useRef(renewalSummaryData);
  renewalSummaryDataRef.current = renewalSummaryData;

  // Keyed by field so two fields changed in one window both report. previousValue is carried
  // forward from the first call, so the pair reads "moved from X to Y" across the whole drag.
  const pendingInteractions = useRef({});
  const flushInteractions = useDebouncedCallback(() => {
    Object.values(pendingInteractions.current).forEach((payload) => {
      // A drag that settles where it started is a net no-op, though no single call looked like one.
      if (payload.selectedValue === payload.previousValue) return;
      getGtmService().pushAdjustCoverInteraction(payload);
    });
    pendingInteractions.current = {};
  }, 350);

  // Flush on unmount so a value settled just before navigating away still reaches the data layer.
  // flushInteractions' identity is stable, so the cleanup registers once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => flushInteractions.flush(), []);

  const trackCoverChange = (fieldName, elementType, selectedValue, previousValue) => {
    if (selectedValue === previousValue) return;
    const pending = pendingInteractions.current[fieldName];
    pendingInteractions.current[fieldName] = {
      policyNumber: renewalSummaryDataRef.current?.PolicyNumber,
      elementType,
      fieldName,
      selectedValue,
      previousValue: pending ? pending.previousValue : previousValue
    };
    flushInteractions();
  };

  const resolveSliderOptionsWithCurrent = ({
    baseOptions,
    currentOption,
    currentCode,
    currentValue,
    codeKey,
    valueKey,
    includeCurrentOption = false
  }) => {
    let options = baseOptions;

    if (includeCurrentOption && currentOption) {
      const hasCurrentValue = baseOptions.some(
        (option) => Number(option[valueKey]) === Number(currentOption[valueKey])
      );
      if (!hasCurrentValue) {
        options = [...baseOptions, currentOption];
      }
    }

    const currentCodeForSlider =
      options.find((option) => String(option[codeKey]) === String(currentCode))?.[codeKey] ||
      options.find((option) => Number(option[valueKey]) === Number(currentValue))?.[codeKey];

    return {
      options,
      currentCodeForSlider
    };
  };

  const getPremiumDetails = (values) => {
    const matchingPremium = renewalQuoteData?.Pets?.[0]?.Premiums?.find(
      (p) =>
        p.ProductCode === PRODUCT_CODE.CORE &&
        p.ExcessCode === values?.ExcessCode &&
        p.LevelOfCoverCode === values?.LevelOfCoverCode
    );

    let newPremium = (matchingPremium?.TotalPremiumAmount ?? 0) + (matchingPremium?.TotalLoadingAmount ?? 0);
    let freeMonthDiscount = matchingPremium?.LessFreeMonthAmount ?? 0;

    if (values?.RoutineCare) {
      const roca = matchingPremium?.OptionalCovers?.find((c) => c.ProductCode === PRODUCT_CODE.ROCA);
      newPremium += (roca?.TotalOptionalCoverAmount ?? 0) + (roca?.TotalOptionalCoverLoadingAmount ?? 0);
      freeMonthDiscount += roca?.LessFreeMonthAmount ?? 0;
    }

    if (values?.BoosterCare) {
      const boca = matchingPremium?.OptionalCovers?.find((c) => c.ProductCode === PRODUCT_CODE.BOCA);
      newPremium += (boca?.TotalOptionalCoverAmount ?? 0) + (boca?.TotalOptionalCoverLoadingAmount ?? 0);
      freeMonthDiscount += boca?.LessFreeMonthAmount ?? 0;
    }

    const paymentFrequencyMultiplier =
      {
        [FREQUENCY_CODE_MONTHLY]: 12,
        [FREQUENCY_CODE_FORTNIGHTLY]: 26,
        [FREQUENCY_CODE_ANNUALLY]: 1
      }[renewalSummaryData?.PaymentFrequency] ?? 1;

    const newPremiumAnnual =
      renewalSummaryData?.PaymentFrequency?.toLowerCase() === FREQUENCY_CODE_ANNUALLY.toLowerCase()
        ? newPremium * paymentFrequencyMultiplier - freeMonthDiscount
        : newPremium * paymentFrequencyMultiplier;

    return {
      newPremium: Number(newPremium.toFixed(2)),
      freeMonthDiscount: Number(freeMonthDiscount.toFixed(2)),
      newPremiumAnnual: Number(newPremiumAnnual.toFixed(2))
    };
  };

  // Build a combination matrix by joining ExcessLevels and LevelsOfCover on their shared code
  const levelOfCoverByCode = new Map(
    (renewalQuoteData?.Pets?.[0]?.LevelsOfCover ?? []).map((item) => [item.LevelOfCoverCode, item.LevelOfCover])
  );
  const combinations = (renewalQuoteData?.Pets?.[0]?.ExcessLevels ?? [])
    .map((item) => ({
      code: item.ExcessCode,
      excess: item.Excess,
      levelOfCover: levelOfCoverByCode.get(item.ExcessCode)
    }))
    .filter((c) => c.levelOfCover !== undefined);

  const [selectedValues, setSelectedValues] = useState({
    Excess: renewalSummaryData?.Excess,
    ExcessCode: renewalSummaryData?.ExcessNo.toString(),
    BenefitPercentage: renewalSummaryData?.BenefitPercentage,
    LevelOfCoverCode: renewalSummaryData?.LevelOfCoverCode.toString(),
    RoutineCare: renewalSummaryData?.RoutineCare,
    BoosterCare: renewalSummaryData?.BoosterCare,
    BoosterCareName: renewalSummaryData?.BoosterCareName,
    BoosterCareLimit: renewalSummaryData?.BoosterCareLimit
  });

  useEffect(() => {
    const { newPremium, freeMonthDiscount, newPremiumAnnual } = getPremiumDetails(selectedValues);

    dispatch(
      setAdjustedSummary({
        ...selectedValues,
        newPremium,
        freeMonthDiscount,
        newPremiumAnnual,
        isCoverAdjusted: false
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const [popupState, setPopupState] = useState({
    isOpen: false,
    title: '',
    body: ''
  });

  // Filter options to only valid combinations based on the other dimension's current selection
  const baseFilteredExcessOptions = Array.from(
    new Map(
      (selectedValues.BenefitPercentage != null
        ? combinations.filter((c) => c.levelOfCover === selectedValues.BenefitPercentage)
        : combinations
      ).map((c) => [c.excess, { excessCode: c.code, excess: c.excess }])
    ).values()
  );

  const currentExcessCombination = combinations.find((c) => String(c.code) === String(renewalSummaryData?.ExcessNo));
  const currentExcessOption = currentExcessCombination
    ? { excessCode: currentExcessCombination.code, excess: currentExcessCombination.excess }
    : null;
  const { options: filteredExcessOptions, currentCodeForSlider: currentExcessCodeForSlider } =
    resolveSliderOptionsWithCurrent({
      baseOptions: baseFilteredExcessOptions,
      currentOption: currentExcessOption,
      currentCode: renewalSummaryData?.ExcessNo,
      currentValue: renewalSummaryData?.Excess,
      codeKey: 'excessCode',
      valueKey: 'excess',
      includeCurrentOption: false
    });

  const baseFilteredLevelsOfCoverOptions = Array.from(
    new Map(
      (selectedValues.Excess != null
        ? combinations.filter((c) => c.excess === selectedValues.Excess)
        : combinations
      ).map((c) => [c.levelOfCover, { levelOfCoverCode: c.code, levelOfCover: c.levelOfCover }])
    ).values()
  );

  const currentLevelOfCoverCombination = combinations.find(
    (c) => String(c.code) === String(renewalSummaryData?.LevelOfCoverCode)
  );
  const currentLevelOfCoverOption = currentLevelOfCoverCombination
    ? {
        levelOfCoverCode: currentLevelOfCoverCombination.code,
        levelOfCover: currentLevelOfCoverCombination.levelOfCover
      }
    : null;
  const { options: filteredLevelsOfCoverOptions, currentCodeForSlider: currentLevelOfCoverCodeForSlider } =
    resolveSliderOptionsWithCurrent({
      baseOptions: baseFilteredLevelsOfCoverOptions,
      currentOption: currentLevelOfCoverOption,
      currentCode: renewalSummaryData?.LevelOfCoverCode,
      currentValue: renewalSummaryData?.BenefitPercentage,
      codeKey: 'levelOfCoverCode',
      valueKey: 'levelOfCover',
      includeCurrentOption: false
    });

  const commitAdjustedSummary = (updatedValues) => {
    const isCoverAdjusted =
      updatedValues.Excess !== renewalSummaryData?.Excess ||
      updatedValues.BenefitPercentage !== renewalSummaryData?.BenefitPercentage ||
      updatedValues.RoutineCare !== renewalSummaryData?.RoutineCare ||
      updatedValues.BoosterCare !== renewalSummaryData?.BoosterCare ||
      updatedValues.BoosterCareName !== renewalSummaryData?.BoosterCareName ||
      updatedValues.BoosterCareLimit !== renewalSummaryData?.BoosterCareLimit;

    const { newPremium, freeMonthDiscount, newPremiumAnnual } = getPremiumDetails(updatedValues);

    dispatch(
      setAdjustedSummary({
        ...updatedValues,
        newPremium,
        freeMonthDiscount,
        newPremiumAnnual,
        isCoverAdjusted
      })
    );

    setSelectedValues(updatedValues);
  };

  // Tracking only - the state update below must happen for every field either way.
  const trackFieldChange = (field, value) => {
    const fieldName = TRACKED_CHANGE_FIELD_NAMES[field];
    if (!fieldName) return;
    trackCoverChange(fieldName, 'option', value, selectedValues[field]);
  };

  const handleChange = (field, value) => {
    trackFieldChange(field, value);

    let updatedValues = {
      ...selectedValues,
      [field]: value
    };

    // Reset Excess if it's no longer a valid combination with the new BenefitPercentage
    if (field === 'BenefitPercentage') {
      const matchingCombination = combinations.find(
        (c) => c.levelOfCover === value && c.excess === updatedValues.Excess
      );
      if (updatedValues.Excess != null && !matchingCombination) {
        updatedValues = { ...updatedValues, Excess: undefined, ExcessCode: undefined, LevelOfCoverCode: undefined };
      } else if (matchingCombination) {
        updatedValues = {
          ...updatedValues,
          ExcessCode: matchingCombination.code,
          LevelOfCoverCode: matchingCombination.code
        };
      }
    }

    // Reset BenefitPercentage if it's no longer a valid combination with the new Excess
    if (field === 'Excess') {
      const matchingCombination = combinations.find(
        (c) => c.excess === value && c.levelOfCover === updatedValues.BenefitPercentage
      );
      if (updatedValues.BenefitPercentage != null && !matchingCombination) {
        updatedValues = {
          ...updatedValues,
          BenefitPercentage: undefined,
          ExcessCode: undefined,
          LevelOfCoverCode: undefined
        };
      } else if (matchingCombination) {
        updatedValues = {
          ...updatedValues,
          ExcessCode: matchingCombination.code,
          LevelOfCoverCode: matchingCombination.code
        };
      }
    }

    commitAdjustedSummary(updatedValues);
  };

  const handleSliderChange = ({ option, codeField }) => {
    if (!option || option[codeField] == null) return;

    const matchingCombination = combinations.find((c) => String(c.code) === String(option[codeField]));

    if (codeField === 'excessCode') {
      trackCoverChange('excess', 'slider', matchingCombination?.excess, selectedValues.Excess);
    } else {
      trackCoverChange(
        'benefit_percentage',
        'slider',
        matchingCombination?.levelOfCover,
        selectedValues.BenefitPercentage
      );
    }

    let updatedValues = {
      ...selectedValues,
      Excess: option.excess,
      BenefitPercentage: option.levelOfCover
    };

    if (!matchingCombination) {
      updatedValues = {
        ...updatedValues,
        BenefitPercentage: undefined,
        ExcessCode: undefined,
        LevelOfCoverCode: undefined
      };
    } else {
      updatedValues = {
        ...updatedValues,
        Excess: matchingCombination.excess,
        BenefitPercentage: matchingCombination.levelOfCover,
        ExcessCode: matchingCombination.code,
        LevelOfCoverCode: matchingCombination.code
      };
    }

    commitAdjustedSummary(updatedValues);
  };

  const handleExcessSliderChange = (option) => handleSliderChange({ option, codeField: 'excessCode' });

  const handleLevelOfCoverSliderChange = (option) => handleSliderChange({ option, codeField: 'levelOfCoverCode' });

  const handleClosePopup = () => {
    setPopupState((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const handleTooltipModalLinkClick = (event) => {
      const trigger = event.target.closest('.tooltip-modal-link');
      if (!trigger) return;
      event.preventDefault();
      const title = decodeHtmlEntities(trigger.getAttribute('data-tooltip-modal-title') || 'Details');
      const body = decodeHtmlEntities(trigger.getAttribute('data-tooltip-modal-body') || '');
      getGtmService().pushAdjustCoverInteraction({
        policyNumber: renewalSummaryDataRef.current?.PolicyNumber,
        elementType: 'help_popup',
        fieldName: resolveHelpTopicId(trigger)
      });
      setPopupState({ isOpen: true, title, body });
    };
    document.addEventListener('click', handleTooltipModalLinkClick);
    return () => document.removeEventListener('click', handleTooltipModalLinkClick);
  }, [setPopupState]);

  useEffect(() => {
    const closeOpenTooltips = () => {
      document.querySelectorAll('.renewalAdjustCover .tooltip-click-balloon.is-open').forEach((tooltip) => {
        tooltip.classList.remove('is-open');
        tooltip.classList.remove('align-left', 'align-right');
        tooltip.querySelector('.tooltip-icon-trigger')?.setAttribute('aria-expanded', 'false');
      });
    };

    const alignTooltipToViewport = (wrapper) => {
      if (!wrapper) return;

      const content = wrapper.querySelector('.tooltip-click-balloon-content');
      if (!content) return;

      wrapper.classList.remove('align-left', 'align-right');

      const viewportPadding = 12;
      const tooltipRect = content.getBoundingClientRect();
      const overflowsLeft = tooltipRect.left < viewportPadding;
      const overflowsRight = tooltipRect.right > window.innerWidth - viewportPadding;

      if (overflowsLeft && !overflowsRight) {
        wrapper.classList.add('align-left');
        return;
      }

      if (overflowsRight && !overflowsLeft) {
        wrapper.classList.add('align-right');
        return;
      }

      if (overflowsLeft && overflowsRight) {
        const wrapperRect = wrapper.getBoundingClientRect();
        const spaceOnLeft = wrapperRect.left - viewportPadding;
        const spaceOnRight = window.innerWidth - viewportPadding - wrapperRect.right;
        wrapper.classList.add(spaceOnRight >= spaceOnLeft ? 'align-left' : 'align-right');
      }
    };

    const toggleTooltip = (wrapper) => {
      const isOpen = wrapper.classList.contains('is-open');
      closeOpenTooltips();
      const trigger = wrapper.querySelector('.tooltip-icon-trigger');
      if (!isOpen) {
        wrapper.classList.add('is-open');
        trigger?.setAttribute('aria-expanded', 'true');
        window.requestAnimationFrame(() => alignTooltipToViewport(wrapper));
        // The wrapper always carries the item id; trigger may be null.
        getGtmService().pushAdjustCoverInteraction({
          policyNumber: renewalSummaryDataRef.current?.PolicyNumber,
          elementType: 'help_balloon',
          fieldName: resolveHelpTopicId(wrapper)
        });
      }
    };

    const handleViewportChange = () => {
      document.querySelectorAll('.renewalAdjustCover .tooltip-click-balloon.is-open').forEach((tooltip) => {
        alignTooltipToViewport(tooltip);
      });
    };

    const handleTooltipTriggerClick = (event) => {
      const trigger = event.target.closest('.renewalAdjustCover .tooltip-icon-trigger');
      if (trigger) {
        event.preventDefault();
        const wrapper = trigger.closest('.tooltip-click-balloon');
        if (!wrapper) return;
        toggleTooltip(wrapper);
        return;
      }

      const clickedInsideTooltip = event.target.closest('.renewalAdjustCover .tooltip-click-balloon');
      if (!clickedInsideTooltip) closeOpenTooltips();
    };

    const handleTooltipTriggerKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      closeOpenTooltips();
    };

    document.addEventListener('click', handleTooltipTriggerClick);
    document.addEventListener('keydown', handleTooltipTriggerKeyDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    return () => {
      document.removeEventListener('click', handleTooltipTriggerClick);
      document.removeEventListener('keydown', handleTooltipTriggerKeyDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, []);

  return {
    filteredExcessOptions,
    filteredLevelsOfCoverOptions,
    currentExcessCodeForSlider,
    currentLevelOfCoverCodeForSlider,
    handleChange,
    handleExcessSliderChange,
    handleLevelOfCoverSliderChange,
    popupState,
    selectedValues,
    handleClosePopup
  };
};
